import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { cn, formatPrice } from "@apex/shared";
import { useWidget } from "../../state";
import { CatalogItemList } from "../CatalogItemList";
import { DirtyStateDialog } from "../DirtyStateDialog";
import { ImageLightbox } from "../ImageLightbox";
import { SearchInput } from "../SearchInput";
import { PersistentSummaryPanel } from "../PersistentSummaryPanel";
import { Spinner } from "../ui/Spinner";
import { WidgetPortal } from "../WidgetPortal";
import { containerIsScroller, readStickyOffsetPx } from "../../utils/scroll";

export interface ConfigureViewProps {
  className?: string;
}

/** Keys to exclude from the spec display (internal/price-related). */
const HIDDEN_SPEC_KEYS = new Set([
  "baseprice", "price", "msrp", "currency", "basevehicle", "vehiclemake", "vehicletype",
]);

/** Priority order for spec keys (shown first in this order). */
const SPEC_PRIORITY: string[] = [
  "workingHeight", "workingOutreach", "workingLoad", "baseWeight",
  "boomRotation", "basket", "basketSize", "outriggers", "boom", "engineType",
];


function formatSpecKey(key: string): string {
  const labels: Record<string, string> = {
    workingHeight: "Working Height",
    workingOutreach: "Outreach",
    workingLoad: "Max Load",
    baseWeight: "Weight",
    boomRotation: "Rotation",
    basket: "Basket",
    basketSize: "Basket Size",
    outriggers: "Outriggers",
    boom: "Boom",
    engineType: "Engine",
  };
  if (labels[key]) return labels[key];

  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function formatSpecValue(key: string, value: string): string {
  if (key === "boomRotation" && !value.includes("\u00B0")) return `${value}\u00B0`;
  return value;
}

/** Chevron-left SVG used in both back buttons */
function ChevronLeft({ size = 18 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function ConfigureView({ className }: ConfigureViewProps) {
  const { state, goBack, setAccessorySearchTerm } = useWidget();
  const { selectedRootItem, isLoading, selectedItems, accessorySearchTerm } = state;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [confirmChangeModelOpen, setConfirmChangeModelOpen] = useState(false);

  /**
   * "Change model" intent.
   *
   * If the user has chosen any accessories we must surface the dirty-state
   * confirmation dialog before discarding their work. Otherwise navigate
   * straight back to model selection.
   */
  const handleChangeModelClick = useCallback(() => {
    if (selectedItems.size > 0) {
      setConfirmChangeModelOpen(true);
      return;
    }
    goBack();
  }, [selectedItems, goBack]);

  const handleConfirmChangeModel = useCallback(() => {
    setConfirmChangeModelOpen(false);
    goBack();
  }, [goBack]);

  const handleCancelChangeModel = useCallback(() => {
    setConfirmChangeModelOpen(false);
  }, []);
  const heroRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);

  // Intersection Observer — detect when hero scrolls out of view (drives the
  // compact bar). In fixed mode the internal container is the scrollport; in
  // auto (page-flow) mode it does not scroll, so observe against the viewport
  // (root:null) and offset the top by the host header (--apex-sticky-offset).
  useEffect(() => {
    const hero = heroRef.current;
    const container = scrollContainerRef.current;
    if (!hero || !container) return;

    const useContainer = containerIsScroller(container);
    const offset = useContainer ? 0 : readStickyOffsetPx(container);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      {
        root: useContainer ? container : null,
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [selectedRootItem]);

  // Extract displayable spec entries, sorted by priority
  const specs = useMemo(() => {
    if (!selectedRootItem?.metadata) return [];
    const entries = Object.entries(selectedRootItem.metadata);

    const visible = entries.filter(
      ([key]) => !HIDDEN_SPEC_KEYS.has(key.toLowerCase())
    );

    visible.sort((a, b) => {
      const ai = SPEC_PRIORITY.indexOf(a[0]);
      const bi = SPEC_PRIORITY.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return visible.slice(0, 8);
  }, [selectedRootItem]);

  // Top 4 specs for inline display, top 3 for sticky bar
  const heroSpecs = useMemo(() => specs.slice(0, 4), [specs]);
  const stickySpecs = useMemo(() => specs.slice(0, 3), [specs]);

  const prevImage = useCallback(() => {
    setActiveImageIndex((i) =>
      i === 0 ? (selectedRootItem?.images?.length ?? 1) - 1 : i - 1
    );
  }, [selectedRootItem]);

  const nextImage = useCallback(() => {
    setActiveImageIndex((i) =>
      i === (selectedRootItem?.images?.length ?? 1) - 1 ? 0 : i + 1
    );
  }, [selectedRootItem]);

  if (isLoading || !selectedRootItem) {
    return (
      <div className="apex-flex apex-items-center apex-justify-center apex-min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const images = selectedRootItem.images || [];
  const hasMultipleImages = images.length > 1;

  return (
    <div
      className={cn(
        // The sidebar summary renders from md (PersistentSummaryPanel hides the
        // mobile bar there), so the row must form at md too - otherwise between
        // md and lg the summary and its CTA sit below ALL the content.
        "apex-flex apex-flex-col md:apex-flex-row apex-h-full",
        className
      )}
    >
      {/* Main Content.
          `apex-min-w-0`: a flex item's automatic minimum is its min-content
          width. In auto (page-flow) embeds this pane's overflow is released
          to visible, so without it the category chip strip (a row of
          shrink-0 chips) sets the pane's minimum and pushes the summary
          panel off the right edge of the host. */}
      <div
        ref={scrollContainerRef}
        className="apex-flex-1 apex-min-w-0 apex-overflow-auto apex-relative"
        data-scroll-container
      >
        {/* ── Sticky Compact Bar ── */}
        <div
          ref={stickyBarRef}
          className={cn(
            "apex-sticky apex-z-20",
            "apex-bg-background/95 apex-backdrop-blur-md",
            "apex-transition-all apex-duration-200 apex-ease-out apex-overflow-hidden",
            showStickyBar
              ? "apex-opacity-100 apex-border-b apex-border-border"
              : "apex-opacity-0 apex-pointer-events-none"
          )}
          style={{
            top: "var(--apex-sticky-offset, 0px)",
            maxHeight: showStickyBar ? "5rem" : "0px",
            boxShadow: showStickyBar ? "var(--apex-shadow-sticky)" : "none",
          }}
        >
          <div className={cn(
            "apex-flex apex-items-center apex-gap-3 apex-px-6 apex-py-2",
            showStickyBar && "apex-animate-slide-down"
          )}>
            {/* Change model — explicit per-step CTA */}
            <button
              type="button"
              onClick={handleChangeModelClick}
              className="apex-shrink-0 apex-inline-flex apex-items-center apex-gap-1 apex-min-h-[36px] apex-px-2 apex-py-1 apex-rounded-md hover:apex-bg-muted apex-transition-colors apex-text-xs apex-font-medium apex-text-muted-foreground hover:apex-text-foreground"
              aria-label="Change model and start over"
            >
              <ChevronLeft size={14} />
              <span>Change model</span>
            </button>

            {/* Thumbnail */}
            {images[0] && (
              <div className="apex-shrink-0 apex-h-9 apex-w-12 apex-rounded apex-overflow-hidden apex-bg-muted apex-flex apex-items-center apex-justify-center">
                <img src={images[0]} alt="" className="apex-max-h-full apex-max-w-full apex-object-contain" />
              </div>
            )}

            {/* Item name + specs */}
            <div className="apex-flex-1 apex-min-w-0">
              <p className="apex-font-semibold apex-text-sm apex-text-foreground apex-truncate apex-leading-tight apex-font-display">
                {selectedRootItem.name || selectedRootItem.code}
              </p>
              {stickySpecs.length > 0 && (
                // Hidden below sm: the nowrap spec line is wider than a phone
                // viewport and ran off the right edge of the host page.
                <div className="apex-hidden sm:apex-flex apex-items-center apex-gap-2.5 apex-mt-0.5 apex-overflow-hidden">
                  {stickySpecs.map(([key, value]) => (
                    <span key={key} className="apex-text-[11px] apex-text-muted-foreground apex-whitespace-nowrap">
                      <span className="apex-font-medium apex-text-foreground/70">{formatSpecKey(key)}</span>{" "}
                      {formatSpecValue(key, value)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Price */}
            {selectedRootItem.priceNet != null && selectedRootItem.priceNet > 0 && (
              <span className="apex-text-sm apex-font-bold apex-text-primary apex-shrink-0">
                {formatPrice(selectedRootItem.priceNet, selectedRootItem.currency)}
              </span>
            )}
          </div>
        </div>

        {/* ── Hero Section ── */}
        <div ref={heroRef} className="apex-border-b apex-border-border">
          {/* Change-model CTA + title bar */}
          <div className="apex-flex apex-items-center apex-gap-3 apex-px-6 apex-py-2 apex-border-b apex-border-border">
            <button
              type="button"
              onClick={handleChangeModelClick}
              className="apex-shrink-0 apex-inline-flex apex-items-center apex-gap-1 apex-min-h-[44px] apex-px-2 apex-py-1.5 apex-rounded-md hover:apex-bg-muted apex-transition-colors apex-text-sm apex-font-medium apex-text-muted-foreground hover:apex-text-foreground"
              aria-label="Change model and start over"
            >
              <ChevronLeft size={16} />
              <span>Change model</span>
            </button>
            <h2 className="apex-font-semibold apex-text-foreground apex-truncate apex-leading-tight apex-font-display">
              {selectedRootItem.name || selectedRootItem.code}
            </h2>
          </div>

          {/* Image + specs: stacked below lg (phones, and tablets where the
              sidebar summary already takes a third of the width), side by
              side from lg. */}
          <div className="apex-flex apex-flex-col lg:apex-flex-row">
            {/* Image — fixed proportion */}
            {images.length > 0 && (
              <div className="apex-relative apex-w-full lg:apex-w-[55%] apex-shrink-0" style={{ background: "var(--apex-gradient-surface)" }}>
                <div
                  className="apex-flex apex-items-center apex-justify-center apex-cursor-pointer apex-max-h-[320px] lg:apex-max-h-none"
                  style={{ aspectRatio: "4/3" }}
                  onClick={() => setLightboxOpen(true)}
                  role="button"
                  aria-label="Expand image"
                >
                  <img
                    src={images[activeImageIndex]}
                    alt={selectedRootItem.name || selectedRootItem.code}
                    className="apex-max-h-full apex-max-w-full apex-object-contain apex-p-2"
                  />
                </div>

                {/* Expand button */}
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="apex-absolute apex-bottom-2 apex-right-2 apex-rounded-full apex-bg-background/80 apex-p-1.5 apex-shadow hover:apex-bg-background apex-transition-colors apex-backdrop-blur-sm"
                  aria-label="Expand image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" />
                    <path d="M9 21H3v-6" />
                    <path d="M21 3l-7 7" />
                    <path d="M3 21l7-7" />
                  </svg>
                </button>

                {/* Nav arrows */}
                {hasMultipleImages && (
                  <>
                    <button
                      type="button"
                      onClick={prevImage}
                      className="apex-absolute apex-left-1.5 apex-top-1/2 apex--translate-y-1/2 apex-rounded-full apex-bg-background/80 apex-p-1.5 apex-shadow hover:apex-bg-background apex-transition-colors apex-backdrop-blur-sm"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={nextImage}
                      className="apex-absolute apex-right-1.5 apex-top-1/2 apex--translate-y-1/2 apex-rounded-full apex-bg-background/80 apex-p-1.5 apex-shadow hover:apex-bg-background apex-transition-colors apex-backdrop-blur-sm"
                      aria-label="Next image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image counter */}
                {hasMultipleImages && (
                  <span className="apex-absolute apex-bottom-2 apex-right-2 apex-px-2 apex-py-0.5 apex-rounded apex-bg-background/80 apex-text-[11px] apex-text-muted-foreground apex-backdrop-blur-sm">
                    {activeImageIndex + 1} / {images.length}
                  </span>
                )}
              </div>
            )}

            {/* Specs panel */}
            <div className="apex-flex-1 apex-flex apex-flex-col apex-justify-center apex-p-4 apex-border-t lg:apex-border-t-0 lg:apex-border-l apex-border-border">
              {selectedRootItem.description && (
                <p className="apex-text-xs apex-text-muted-foreground apex-line-clamp-2 apex-mb-3">
                  {selectedRootItem.description}
                </p>
              )}

              {heroSpecs.length > 0 && (
                <div className="apex-grid apex-grid-cols-2 apex-gap-x-4 apex-gap-y-3 apex-stagger">
                  {heroSpecs.map(([key, value]) => (
                    <div key={key}>
                      <p className="apex-text-[10px] apex-uppercase apex-tracking-wider apex-text-muted-foreground apex-font-medium">
                        {formatSpecKey(key)}
                      </p>
                      <p className="apex-text-base apex-font-bold apex-text-foreground apex-leading-tight">
                        {formatSpecValue(key, value)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {selectedRootItem.priceNet != null && selectedRootItem.priceNet > 0 && (
                <div className="apex-mt-3 apex-pt-3 apex-border-t apex-border-border">
                  {/* primary/5 tint: on primary/10 the 12px "From" label
                      measured 4.49:1, just under AA. */}
                  <div className="apex-inline-flex apex-items-baseline apex-gap-1.5 apex-rounded-md apex-bg-primary/5 apex-px-3 apex-py-1.5">
                    <span className="apex-text-xs apex-text-primary apex-font-medium">From</span>
                    <span className="apex-text-lg apex-font-bold apex-text-primary">
                      {formatPrice(selectedRootItem.priceNet, selectedRootItem.currency)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Thumbnail strip */}
          {hasMultipleImages && (
            <div className="apex-flex apex-gap-1.5 apex-px-3 apex-py-2 apex-border-t apex-border-border apex-overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveImageIndex(index)}
                  className={cn(
                    "apex-shrink-0 apex-w-14 apex-h-10 apex-rounded apex-overflow-hidden apex-border-2 apex-transition-colors",
                    index === activeImageIndex
                      ? "apex-border-primary"
                      : "apex-border-transparent hover:apex-border-muted-foreground/50"
                  )}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={image} alt="" className="apex-h-full apex-w-full apex-object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Extended Specs (remaining specs below hero) ── */}
        {specs.length > 4 && (
          <div className="apex-border-b apex-border-border">
            <div
              className="apex-grid apex-gap-px apex-bg-border"
              style={{
                gridTemplateColumns: `repeat(${Math.min(specs.length - 4, 4)}, 1fr)`,
              }}
            >
              {specs.slice(4).map(([key, value]) => (
                <div
                  key={key}
                  className="apex-bg-background apex-px-2 apex-py-2 apex-flex apex-flex-col apex-items-center apex-text-center"
                >
                  <p className="apex-text-[10px] apex-uppercase apex-tracking-wider apex-text-muted-foreground apex-font-medium">
                    {formatSpecKey(key)}
                  </p>
                  <p className="apex-mt-0.5 apex-font-semibold apex-text-sm apex-text-foreground">
                    {formatSpecValue(key, value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Child Items List ── */}
        <div className="apex-px-6 apex-py-4">
          <div className="apex-flex apex-flex-col sm:apex-flex-row sm:apex-items-center sm:apex-justify-between apex-gap-3 apex-mb-4">
            <h2 className="apex-text-lg apex-font-semibold apex-font-display apex-whitespace-nowrap">
              Select Items
            </h2>
            <div className="apex-w-full sm:apex-max-w-xs">
              <SearchInput
                value={accessorySearchTerm}
                onChange={setAccessorySearchTerm}
                placeholder="Search accessories..."
                aria-label="Search accessories"
              />
            </div>
          </div>
          <CatalogItemList stickyNavTop={showStickyBar ? 53 : 0} />
        </div>
      </div>

      {/* ── Sidebar — Persistent Summary Panel (#540) ── */}
      <PersistentSummaryPanel />

      {/* ── Image Lightbox ── */}
      {lightboxOpen && images.length > 0 && (
        <WidgetPortal>
          <ImageLightbox
            images={images}
            initialIndex={activeImageIndex}
            alt={selectedRootItem.name || selectedRootItem.code}
            onClose={() => setLightboxOpen(false)}
          />
        </WidgetPortal>
      )}

      {/* ── Dirty-state confirmation ── */}
      <DirtyStateDialog
        open={confirmChangeModelOpen}
        onCancel={handleCancelChangeModel}
        onConfirm={handleConfirmChangeModel}
      />
    </div>
  );
}
